import { useState } from 'react'
import { TableCell, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown, ChevronRight, Eye, MoreHorizontal, Users } from 'lucide-react'
import { isBefore, parseISO, startOfDay } from 'date-fns'
import { usePermissions } from '@/hooks/usePermissions'
import { useUserNameResolver } from '@/core/hooks/useUserNameResolver'
import { tareasConFechaMovible, useActualizarFechaGrupo } from '@/core/hooks/useTareasGrupo'
import type { GrupoDeTablero } from '@/core/services/tareasGrupoService'
import type { EstadoTarea, TareaCompleta } from '@/types/database'
import { cn } from '@/lib/utils'
import { EditableDateCell } from './EditableDateCell'
import { FechaCompletada } from './FechaCompletada'
import { SinLeerBadge } from './SinLeerBadge'

const ESTADO_STYLES: Record<EstadoTarea, string> = {
  pendiente: 'bg-slate-100 text-slate-800',
  en_proceso: 'bg-blue-100 text-blue-800',
  completada: 'bg-green-100 text-green-800',
}

const ESTADO_LABELS: Record<EstadoTarea, string> = {
  pendiente: 'Pendiente',
  en_proceso: 'En Proceso',
  completada: 'Completada',
}

interface TareaGrupoFilaProps {
  grupo: GrupoDeTablero
  mostrarColumnaAcciones: boolean
  selectionMode: boolean
  selectedIds: Set<string>
  onSelectionChange?: (id: string, selected: boolean) => void
  onVerGrupo?: (grupo: GrupoDeTablero) => void
  onVerDetalle?: (tarea: TareaCompleta) => void
  onCambiarEstado: (tarea: TareaCompleta, estado: EstadoTarea) => void
  puedeEditarFila: (tarea: TareaCompleta) => boolean
  puedeCambiarEstadoFila: (tarea: TareaCompleta) => boolean
  /** Ids de las tareas con un comentario que el usuario no leyó. */
  sinLeer: Set<string>
}

/**
 * Fila del listado de tareas sin filtro de cliente para un grupo de clientes, con una fila por cliente que se despliega
 * debajo. Cada cliente trae un check que marca su tarea terminada, con el permiso de esa tarea.
 * La selección múltiple de la fila del grupo selecciona sus tareas del listado.
 * La fecha de la fila del grupo mueve todas sus tareas sin completar que el usuario puede editar.
 */
export function TareaGrupoFila({
  grupo,
  mostrarColumnaAcciones,
  selectionMode,
  selectedIds,
  onSelectionChange,
  onVerGrupo,
  onVerDetalle,
  onCambiarEstado,
  puedeEditarFila,
  puedeCambiarEstadoFila,
  sinLeer,
}: TareaGrupoFilaProps) {
  const [abierto, setAbierto] = useState(false)
  const { esAdmin, puede, emailUsuario } = usePermissions()
  const resolveNombre = useUserNameResolver()
  const actualizarFecha = useActualizarFechaGrupo()

  const { enTablero, visibles } = grupo
  const primera = [...visibles].sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''))[0]
  const porCliente = [...visibles].sort((a, b) =>
    (a.cliente_nombre ?? '').localeCompare(b.cliente_nombre ?? '', 'es')
  )
  const terminadas = visibles.filter((t) => t.estado === 'completada').length
  const asignados = [...new Set(visibles.flatMap((t) => (t.asignado_a ? [t.asignado_a] : [])))]
  const fechas = [...new Set(enTablero.map((t) => t.fecha_limite))]
  const today = startOfDay(new Date())
  const estaVencida = enTablero.some(
    (t) => t.fecha_limite && t.estado !== 'completada' && isBefore(parseISO(`${t.fecha_limite}T00:00:00`), today)
  )
  const todasSeleccionadas = enTablero.every((t) => selectedIds.has(t.id))
  const fechaMovible = tareasConFechaMovible(visibles, puede, emailUsuario).length > 0

  return (
    <>
      <TableRow
        className={cn(
          'hover:bg-muted/50',
          !selectionMode && 'cursor-pointer',
          estaVencida && 'bg-red-50 dark:bg-red-950/30',
          selectionMode && todasSeleccionadas && 'bg-accent/10'
        )}
        onClick={() => !selectionMode && setAbierto((v) => !v)}
      >
        {selectionMode && (
          <TableCell onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={todasSeleccionadas}
              onCheckedChange={(checked) =>
                enTablero.forEach((t) => onSelectionChange?.(t.id, checked === true))
              }
              aria-label={`Seleccionar ${primera?.titulo ?? 'grupo'}`}
            />
          </TableCell>
        )}
        <TableCell>
          <EditableDateCell
            fechaLimite={fechas.length === 1 ? fechas[0] : null}
            estado={fechaMovible ? 'pendiente' : 'completada'}
            canEdit={fechaMovible}
            emptyLabel={fechas.length > 1 ? 'Varias fechas' : '-'}
            onGuardar={(fecha) => actualizarFecha.mutate({ tareas: visibles, fecha })}
          />
        </TableCell>
        <TableCell>
          <Badge variant="outline" className="text-xs whitespace-nowrap">
            {terminadas} de {visibles.length} terminadas
          </Badge>
        </TableCell>
        <TableCell>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-1 py-0.5 -ml-1 font-normal"
            aria-expanded={abierto}
            onClick={(e) => {
              e.stopPropagation()
              setAbierto((v) => !v)
            }}
          >
            {abierto ? <ChevronDown className="h-4 w-4 mr-1" /> : <ChevronRight className="h-4 w-4 mr-1" />}
            {visibles.length} clientes{esAdmin ? '' : ', de los que ves'}
          </Button>
        </TableCell>
        <TableCell>
          <button
            type="button"
            data-grupo-clave={grupo.clave}
            className="flex items-center gap-2 text-left rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={(e) => {
              e.stopPropagation()
              onVerGrupo?.(grupo)
            }}
          >
            <span className="font-medium line-clamp-1 hover:underline">{primera?.titulo}</span>
            <Badge variant="outline" className="text-xs gap-1 shrink-0">
              <Users className="h-3 w-3" />
              Grupo
            </Badge>
          </button>
        </TableCell>
        <TableCell>
          {visibles.some((t) => sinLeer.has(t.id)) ? (
            <SinLeerBadge />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell>
          {asignados.length === 0 ? (
            <span className="text-muted-foreground">Sin asignar</span>
          ) : asignados.length === 1 ? (
            resolveNombre(asignados[0])
          ) : (
            `${asignados.length} responsables`
          )}
        </TableCell>
        <TableCell>
          <span className="text-muted-foreground">—</span>
        </TableCell>
        {mostrarColumnaAcciones && (
          <TableCell>
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    onVerGrupo?.(grupo)
                  }}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Ver detalle
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableCell>
        )}
      </TableRow>

      {abierto &&
        porCliente.map((tarea) => {
          const terminada = tarea.estado === 'completada'
          const cliente = tarea.cliente_nombre ?? 'Sin cliente'
          return (
            <TableRow key={tarea.id} className="bg-muted/30 hover:bg-muted/50">
              {selectionMode && <TableCell />}
              <TableCell>
                <EditableDateCell
                  tareaId={tarea.id}
                  fechaLimite={tarea.fecha_limite}
                  estado={tarea.estado}
                  canEdit={puedeEditarFila(tarea)}
                />
              </TableCell>
              <TableCell>
                <Badge variant="secondary" className={cn('text-xs', ESTADO_STYLES[tarea.estado as EstadoTarea])}>
                  {ESTADO_LABELS[tarea.estado as EstadoTarea]}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2 pl-5">
                  <Checkbox
                    checked={terminada}
                    disabled={!puedeCambiarEstadoFila(tarea)}
                    onCheckedChange={(valor) =>
                      onCambiarEstado(tarea, valor === true ? 'completada' : 'pendiente')
                    }
                    aria-label={`Terminado: ${cliente}`}
                  />
                  <button
                    type="button"
                    data-tarea-id={tarea.id}
                    className={cn(
                      'text-left hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      terminada && 'line-through text-muted-foreground'
                    )}
                    onClick={() => onVerDetalle?.(tarea)}
                  >
                    {cliente}
                  </button>
                </div>
              </TableCell>
              <TableCell />
              <TableCell>{sinLeer.has(tarea.id) && <SinLeerBadge />}</TableCell>
              <TableCell>
                {tarea.asignado_a ? (
                  resolveNombre(tarea.asignado_a)
                ) : (
                  <span className="text-muted-foreground">Sin asignar</span>
                )}
              </TableCell>
              <TableCell>
                <FechaCompletada fecha={tarea.fecha_completada} />
              </TableCell>
              {mostrarColumnaAcciones && <TableCell />}
            </TableRow>
          )
        })}
    </>
  )
}
